import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import Button from 'components/form/Button.component';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { isEnvDev } from 'utils/env/determine.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import beConsultationFormsRead from 'settings/consultation-forms/beConsultationForms.action';
import { feConsultationFormsFetchSuccess } from 'settings/consultation-forms/consultationForms.action';
import { selectConsultationForms, selectConsultationFormsHasHydratedFromRead } from 'settings/consultation-forms/consultationForms.selectors';
import { FormsTable } from 'settings/consultation-forms/list/FormsTable.component';
import { NewFormTemplateModal } from 'settings/consultation-forms/modals/NewFormTemplateModal.component';

import CN from './FormsListPage.module.scss';

export const FormsListPage = () => {
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const forms = useSelector(selectConsultationForms);
  const hasHydratedFromRead = useSelector(selectConsultationFormsHasHydratedFromRead);
  const { val: isTemplateModalOpen, yay: openTemplateModal, nay: closeTemplateModal } = useTip(false);
  const isDevMode = isEnvDev();

  const { qj, isLoading } = usePrepareReadOnly(beConsultationFormsRead);

  useEffect(() => {
    if (!qj?.forms) return;

    // In mock/dev mode, preserve in-session edits after initial hydration.
    // TODO: remove this once we get the BE ready for real-time updates.
    if (isDevMode && hasHydratedFromRead) return;

    dispatch(feConsultationFormsFetchSuccess(qj.forms));
  }, [qj?.forms, dispatch, hasHydratedFromRead, isDevMode]);

  return (
    <div
      data-bem="FormsListPage"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.header}>
        <div className={CN.titleSection}>
          <h1 className={CN.title}>{t('consultationForms.title')}</h1>
          <p className={CN.subtitle}>{t('consultationForms.subtitle')}</p>
        </div>
        {isFullArray(forms) && (
          <Button variant="primaryForce" onClick={openTemplateModal} tag="FormsListPage__newForm">
            {t('consultationForms.newForm')}
          </Button>
        )}
      </div>

      <div className={CN.banner}>
        <div className={CN.bannerContent}>
          <h3 className={CN.bannerTitle}>{t('consultationForms.banner.title')}</h3>
          <div className={CN.bannerBulletsWrap}>
            <Bullets tag="FormsListPage__bannerBullets" checkmarkViolet data={[t('consultationForms.banner.bullet1'), t('consultationForms.banner.bullet2'), t('consultationForms.banner.bullet3')]} />
          </div>
          <div className={CN.bannerButtons}>
            <Button variant="primaryForce" onClick={openTemplateModal} tag="FormsListPage__bannerCreate">
              {t('consultationForms.banner.createForm')}
            </Button>
            <Button variant="secondaryForce" tag="FormsListPage__bannerLearn">
              {t('consultationForms.banner.learnMore')}
            </Button>
          </div>
        </div>
        <div className={CN.bannerImage}>
          <div className={CN.bannerImagePlaceholder} />
        </div>
      </div>

      <FormsTable forms={forms} isLoading={isLoading} openTemplateModal={openTemplateModal} />

      <NewFormTemplateModal isOpen={isTemplateModalOpen} onClose={closeTemplateModal} />
    </div>
  );
};
